/**
 * Lectura de la configuracion de entorno, en un solo lugar.
 *
 * Regla: ningun otro archivo lee `import.meta.env`. Asi la lista de variables
 * que la consola necesita se puede leer de un vistazo y no hay que barrer el
 * codigo para saber que hay que configurar.
 */

const texto = (valor: string | undefined): string => (valor ?? '').trim();

/**
 * ⚠️ El caso vacío se trata primero A PROPÓSITO: `Number('')` es `0`, no `NaN`.
 * Sin esta guarda, una variable sin definir no cae al valor por defecto — se
 * convierte en cero, y el cero es un valor plausible. Se ve en pantalla: la
 * página del mapa decía «se refresca cada 0 s» y el centro del mapa quedaba en
 * el golfo de Guinea. Un error que produce un número creíble no se nota.
 */
const numero = (valor: string | undefined, porDefecto: number): number => {
  const crudo = texto(valor);
  if (crudo === '') return porDefecto;
  const n = Number(crudo);
  return Number.isFinite(n) ? n : porDefecto;
};

export type Ambiente = 'staging' | 'produccion' | 'sin definir';

const ambienteLeido = texto(import.meta.env.VITE_AMBIENTE);
const ambiente: Ambiente =
  ambienteLeido === 'produccion'
    ? 'produccion'
    : ambienteLeido === 'staging'
      ? 'staging'
      : 'sin definir';

export const entorno = {
  ambiente,

  /** Base de la API del backend. Vacia mientras el backend no exista. */
  apiUrl: texto(import.meta.env.VITE_API_URL),

  mapa: {
    /**
     * URL del style JSON de MapLibre. Desde 2026-09-09 lo sirve el propio `apps/api`
     * (`GET /mapa/estilo.json`, `docs/04` §5.5) — el valor esperado es
     * `${VITE_API_URL}/mapa/estilo.json`. Vacía mientras `VITE_API_URL` no esté configurada
     * (`hayMapa` gatea sobre esto, no sobre `hayBackend`: las dos hoy dependen de la MISMA
     * variable, pero son preguntas distintas y conviene no fusionarlas).
     */
    estiloUrl: texto(import.meta.env.VITE_MAPA_ESTILO_URL),
    centro: {
      lon: numero(import.meta.env.VITE_MAPA_CENTRO_LON, -77.0428),
      lat: numero(import.meta.env.VITE_MAPA_CENTRO_LAT, -12.0464),
    },
    zoom: numero(import.meta.env.VITE_MAPA_ZOOM, 12),
    /**
     * Atribucion del dato del mapa. **No es un pie de pagina opcional: es condicion de la
     * licencia ODbL**, y la ODbL obliga por el DATO, no por el mapa — un ETA de Valhalla
     * tambien es dato derivado de OSM.
     *
     * 🔴 **CORREGIDO 2026-09-06: el default decia `© OpenStreetMap` y estaba INCOMPLETO en dos
     * formas.** Faltaba `contributors` (la ODbL lo pide literal) y faltaba **OpenMapTiles
     * entero**. No es interpretacion: se leyo el `attribution` del `.pmtiles` que servimos
     * (`metadata` del propio archivo), y trae DOS creditos, no uno. El texto de abajo es ese
     * campo COPIADO LITERAL.
     *
     * 🔑 **Y copiarlo literal tiene una razon tecnica, no solo legal:** si el estilo declara la
     * source con su `attribution`, MapLibre ya la muestra; `customAttribution` AGREGA. El control
     * deduplica por string EXACTO ⇒ con el texto identico aparece una sola vez, y con un texto
     * propio parecido aparecerian las dos.
     */
    atribucion:
      texto(import.meta.env.VITE_MAPA_ATRIBUCION) ||
      '<a href="https://www.openmaptiles.org/" target="_blank">&copy; OpenMapTiles</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap contributors</a>',
    refrescoMs: numero(import.meta.env.VITE_MAPA_REFRESCO_MS, 5_000),
  },
} as const;

/**
 * Hay backend configurado.
 *
 * ⚠️ Dice que hay una URL, NO que responda. La consola no puede afirmar lo
 * segundo sin llamarlo, y todavia no hay a quien llamar. Mientras esto sea
 * `false`, todo lo que se ve en pantalla son datos de ejemplo y la consola lo
 * anuncia sin que se pueda cerrar el aviso.
 */
export const hayBackend = entorno.apiUrl !== '';

/** Hay un servidor de teselas configurado. Mismo limite que arriba: es la URL, no una respuesta. */
export const hayMapa = entorno.mapa.estiloUrl !== '';
